import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api";

const STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const formatAddress = (addr, name) => {
  if (!addr) return null;
  const parts = [
    name,
    addr.line1,
    addr.line2,
    [addr.postal_code, addr.city].filter(Boolean).join(" "),
    addr.state,
    addr.country,
  ].filter(Boolean);
  return parts;
};

const statusColor = (s) => ({
  paid: "text-green-700",
  pending: "text-[color:var(--ink-mute)]",
  failed: "text-red-600",
  expired: "text-red-600",
  refunded: "text-orange-600",
}[s] || "text-[color:var(--ink-mute)]");

const OrdersInbox = () => {
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all"); // all | paid | pending
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ fulfillment_status: "pending", tracking_number: "", notes: "" });

  const load = async () => {
    try {
      const list = await api.listOrders();
      setOrders(list);
      if (selected) {
        const updated = list.find((o) => o.session_id === selected.session_id);
        if (updated) applySelected(updated);
      }
    } catch {
      setOrders([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applySelected = (o) => {
    setSelected(o);
    setDraft({
      fulfillment_status: o.fulfillment_status || "pending",
      tracking_number: o.tracking_number || "",
      notes: o.notes || "",
    });
  };

  const filtered = useMemo(() => {
    if (!orders) return [];
    if (filter === "paid") return orders.filter((o) => o.payment_status === "paid");
    if (filter === "pending") return orders.filter((o) => o.payment_status !== "paid");
    return orders;
  }, [orders, filter]);

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.updateOrderFulfillment(selected.session_id, draft);
      await load();
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (orders === null) return <p className="p-8 font-serif text-[color:var(--ink-mute)]">Loading…</p>;

  const totalPaid = orders.filter((o) => o.payment_status === "paid").length;

  return (
    <div className="grid grid-cols-12 min-h-[70vh]">
      <aside className="col-span-12 md:col-span-5 border-r border-[color:var(--line)] p-5">
        <div className="flex items-baseline justify-between mb-4">
          <p className="font-serif text-2xl">Orders</p>
          <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">
            {orders.length} total · {totalPaid} paid
          </span>
        </div>

        <div className="flex gap-4 mb-4">
          {[
            { k: "all", l: "All" },
            { k: "paid", l: "Paid" },
            { k: "pending", l: "Pending" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`font-serif text-[13px] tlink ${filter === f.k ? "tlink-active" : "tlink-mute"}`}
              data-testid={`orders-filter-${f.k}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="font-serif text-[13px] text-[color:var(--ink-mute)] italic">
            No orders in this view.
          </p>
        )}

        <ul className="space-y-1" data-testid="orders-list">
          {filtered.map((o) => (
            <li key={o.session_id}>
              <button
                onClick={() => applySelected(o)}
                className={`w-full text-left p-3 border ${
                  selected?.session_id === o.session_id
                    ? "border-[color:var(--ink)] bg-[color:var(--paper-2)]"
                    : "border-transparent hover:bg-[color:var(--paper-2)]"
                }`}
                data-testid={`order-item-${o.session_id}`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-serif text-[14px] leading-tight">
                    {o.customer_name || o.customer_email || o.email || "Guest"}
                  </p>
                  <span className="font-serif text-[13px] counter whitespace-nowrap">
                    €{Number(o.amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-3 mt-1">
                  <p className={`font-serif text-[12px] ${statusColor(o.payment_status)}`}>
                    {o.payment_status}
                    {o.payment_status === "paid" && o.fulfillment_status && (
                      <span className="text-[color:var(--ink-mute)]"> · {o.fulfillment_status}</span>
                    )}
                  </p>
                  <span className="font-serif text-[11px] text-[color:var(--ink-mute)]">
                    {formatDate(o.created_at).split(",")[0]}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="col-span-12 md:col-span-7 p-6 md:p-8" data-testid="order-detail">
        {!selected ? (
          <div className="text-center text-[color:var(--ink-mute)] font-serif py-20">
            Select an order from the list to view its details.
          </div>
        ) : (
          <div className="max-w-2xl space-y-8">
            <div>
              <p className="font-serif italic text-[color:var(--ink-mute)] text-sm">
                Order · {formatDate(selected.created_at)}
              </p>
              <h2 className="font-serif text-3xl mt-2">
                {selected.customer_name || selected.customer_email || selected.email || "Guest"}
              </h2>
              {selected.customer_email && (
                <a href={`mailto:${selected.customer_email}`} className="font-serif tlink text-sm">
                  {selected.customer_email}
                </a>
              )}
              <p className={`font-serif text-sm mt-2 ${statusColor(selected.payment_status)}`}>
                Payment: {selected.payment_status}
              </p>
            </div>

            {/* Items */}
            <div className="border-t border-[color:var(--line)] pt-5">
              <p className="font-serif italic text-[color:var(--ink-mute)] text-sm mb-3">Items</p>
              <ul className="space-y-2">
                {(selected.items || []).map((it, i) => (
                  <li key={i} className="flex items-baseline justify-between font-serif">
                    <span>
                      {it.title}
                      <span className="text-[color:var(--ink-mute)]"> × {it.quantity}</span>
                    </span>
                    <span className="counter">€{Number(it.price * it.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-[color:var(--line)] font-serif">
                <span>Total</span>
                <span className="counter text-lg">€{Number(selected.amount || 0).toFixed(2)}</span>
              </div>
            </div>

            {/* Shipping */}
            {selected.shipping_address && (
              <div className="border-t border-[color:var(--line)] pt-5">
                <p className="font-serif italic text-[color:var(--ink-mute)] text-sm mb-3">Shipping to</p>
                <div className="font-serif text-[15px] leading-[1.5]">
                  {formatAddress(selected.shipping_address, selected.shipping_name).map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Fulfillment editor */}
            {selected.payment_status === "paid" && (
              <div className="border-t border-[color:var(--line)] pt-5">
                <p className="font-serif italic text-[color:var(--ink-mute)] text-sm mb-3">Fulfillment</p>

                <label className="block">
                  <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">Status</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => setDraft((d) => ({ ...d, fulfillment_status: s.key }))}
                        className={`font-serif text-[13px] px-3 py-1.5 border ${
                          draft.fulfillment_status === s.key
                            ? "border-[color:var(--ink)] bg-[color:var(--ink)] text-white"
                            : "border-[color:var(--line)] hover:border-[color:var(--ink)]"
                        }`}
                        data-testid={`order-status-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </label>

                <label className="block mt-4">
                  <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">Tracking number</span>
                  <input
                    value={draft.tracking_number}
                    onChange={(e) => setDraft((d) => ({ ...d, tracking_number: e.target.value }))}
                    placeholder="e.g. DHL 123456789"
                    className="mt-1 w-full bg-transparent border-b border-[color:var(--ink)] py-1.5 outline-none font-serif text-[14px]"
                    data-testid="order-tracking-input"
                  />
                </label>

                <label className="block mt-4">
                  <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">Internal notes</span>
                  <textarea
                    rows={2}
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                    className="mt-1 w-full bg-transparent border border-[color:var(--line)] p-2 outline-none font-serif text-[14px] resize-none"
                  />
                </label>

                <div className="mt-5 flex items-center gap-3">
                  <button
                    onClick={onSave}
                    disabled={saving}
                    className="btn-sober disabled:opacity-40"
                    data-testid="order-save-button"
                  >
                    {saving ? "Saving…" : "Save fulfillment"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default OrdersInbox;
